import { Component, DoCheck, HostListener, Input, IterableDiffers, OnDestroy } from '@angular/core';
import { ElementAttribute, ElementTagName, positionToolList, positonOption } from '@models/editor';
import { CanvasService } from '@service/canvas/canvas.service';
import {
    ElementPositionHandleHelperService,
    PositionAxis,
} from '@service/canvas/element-position-handle-helper.service';
import { CanvasInputMode } from '@service/canvas/canvas-input-mode.service';
import { defaultNumberInputArrowedKey, KeyFilterSerivce } from '@service/key-filter.service';
import { mixedString } from 'icreate/common/models/mimic';

@Component({
    selector: 'app-position-options',
    templateUrl: './position-options.component.html',
    styleUrls: ['./../design-side-menu/design-side-menu.component.css'],
    standalone: false,
})
export class PositionOptionsComponent implements DoCheck, OnDestroy {
    readonly ALLOWED_KEYS = defaultNumberInputArrowedKey;
    readonly POSITION_TOOL_LIST = positionToolList;
    readonly TRANSLATE_PREFIX = 'mimic.design.position.label';
    readonly MIXED_STRING = mixedString;

    readonly POSITION_X = PositionAxis.positionX;
    readonly POSITION_Y = PositionAxis.positionY;

    @Input() selectedElements;

    xPosition: string;
    yPosition: string;

    iterableDiffer;
    observer: MutationObserver;

    xMax: number;
    yMax: number;

    isGTagDragActionActive: boolean = false;
    private skipObserverUpdate: boolean = false;

    constructor(
        private iterableDiffers: IterableDiffers,
        private canvasService: CanvasService,
        private keyFilterService: KeyFilterSerivce,
        private elementPositionHandleService: ElementPositionHandleHelperService
    ) {
        this.iterableDiffer = iterableDiffers.find([]).create(null);
    }

    @HostListener('window:mousedown', ['$event'])
    initGroupMoveEvent(event: MouseEvent) {
        if (this.selectedElements.length !== 1) return;
        if (this.selectedElements[0].tagName === ElementTagName.g) {
            this.checkCurrentPosition();
            this.isGTagDragActionActive = true;
        }
    }

    @HostListener('window:mouseup', ['$event'])
    removeGroupMoveEvent(event: MouseEvent) {
        if (this.selectedElements.length !== 1) return;
        if (this.selectedElements[0].tagName === ElementTagName.g && this.isGTagDragActionActive) {
            this.isGTagDragActionActive = false;
        }
    }

    ngOnInit(): void {
        const canvasBgElem = document.getElementById('canvasBackground');

        this.xMax = Number(canvasBgElem.getAttribute(ElementAttribute.width));
        this.yMax = Number(canvasBgElem.getAttribute(ElementAttribute.height));
    }

    ngDoCheck() {
        let changes = this.iterableDiffer.diff(this.selectedElements);
        if (changes) {
            if (this.selectedElements.length > 1) {
                this.xPosition = mixedString;
                this.yPosition = mixedString;
                return;
            }
            this.checkCurrentPosition();

            if (this.selectedElements.length === 1) {
                this.observer?.disconnect();
                this.observer = new MutationObserver(() => {
                    if (this.skipObserverUpdate) {
                        this.skipObserverUpdate = false;
                        return;
                    }
                    this.checkCurrentPosition();
                });

                if (this.selectedElements[0].tagName === ElementTagName.g) {
                    this.observer.observe(this.selectedElements[0], {
                        attributes: true,
                        attributeFilter: ['transform'],
                    });
                } else {
                    this.observer.observe(this.selectedElements[0], {
                        attributes: true,
                        attributeFilter: [
                            ElementAttribute.x,
                            ElementAttribute.y,
                            ElementAttribute.x1,
                            ElementAttribute.y1,
                            ElementAttribute.cx,
                            ElementAttribute.cy,
                            ElementAttribute.d,
                        ],
                    });
                }
            }
        }
    }

    ngOnDestroy(): void {
        this.observer?.disconnect();
    }

    handlePositionChange({ name, value }) {
        this.skipObserverUpdate = true;
        this.elementPositionHandleService.handlePositionChange(name, value, this.selectedElements);
    }

    checkCurrentPosition() {
        const element = this.selectedElements[0];
        this.xPosition = element.getAttribute(ElementAttribute.x);
        this.yPosition = element.getAttribute(ElementAttribute.y);
        if (!this.xPosition || !this.yPosition) {
            if (element.tagName === ElementTagName.g) {
                const layerElement = element.parentElement as unknown as SVGGraphicsElement;
                const layerCTM = layerElement?.getScreenCTM?.();
                if (layerCTM) {
                    const rect = (element as unknown as Element).getBoundingClientRect();
                    const inv = layerCTM.inverse();
                    const tl = new DOMPoint(rect.left, rect.top).matrixTransform(inv);
                    this.xPosition = String(Math.round(tl.x));
                    this.yPosition = String(Math.round(tl.y));
                    return;
                }

                const elementType = element.getAttribute(ElementAttribute.iCreateType);
                const isPump = elementType === CanvasInputMode.DEFAULT_PUMP;
                const isValve = elementType === CanvasInputMode.DEFAULT_VALVE;
                const isFan = elementType === CanvasInputMode.DEFAULT_FAN;

                const bbox = element.getBBox();
                if (isPump || isValve || isFan) {
                    const current = this.elementPositionHandleService.getCurrentTranslate(element);
                    this.xPosition = String(Math.round(bbox.x + current.x));
                    this.yPosition = String(Math.round(bbox.y + current.y));
                } else {
                    this.xPosition = String(Math.round(bbox.x));
                    this.yPosition = String(Math.round(bbox.y));
                }
                return;
            }
            const bbox = element.getBBox();
            this.xPosition = String(Math.round(bbox.x));
            this.yPosition = String(Math.round(bbox.y));
        }
    }

    moveElement({ name, svgIcon }) {
        const canvas: any = this.canvasService.getCanvas();
        switch (name) {
            case positonOption.forward:
                canvas.moveUpDownSelected('Up');
                break;
            case positonOption.front:
                canvas.moveToTopSelectedElement();
                break;
            case positonOption.backwards:
                canvas.moveUpDownSelected('Down');
                break;
            case positonOption.back:
                canvas.moveToBottomSelectedElement();
                break;
            default:
                console.error('position not found');
        }
    }

    validPositionKeyDown(eventObj: { event: KeyboardEvent; value }) {
        const { event, value } = eventObj;
        const result = this.keyFilterService.shouldPreventDefault(event, this.ALLOWED_KEYS);
        if (result) event.preventDefault();
    }
}
